import { Dialog, Transition } from "@headlessui/react";
import { PRIMARY_BUTTON } from "@/constants";
import { Fragment, useEffect, useRef, useState } from "react";
import { PrinterIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import Input from "@/app/components/ui/Inputs/Input";
import { useTRFGenerateAPI } from "@/app/hooks/api_hooks/LabReport/useTRFGenerateAPI";
import { useTRFUpdateAPI } from "@/app/hooks/api_hooks/LabReport/useTRFUpdateAPI";
import { useGenerateVialBarcodeAPI } from "@/app/hooks/api_hooks/LabReport/useGenerateVialBarcodeAPI";
import { ITRFRequisitionVialMapping } from "@/api_framework/api_modals/LabReport";
import { printBarcode } from "@/app/utils/printBarcode";
import CreatableSelect from "react-select/creatable";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import clsx from "clsx";
import { useNavigate } from "react-router";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "36px",
    border: "1px solid grey-20",
    boxShadow: "none",
    backgroundColor: "white",
    borderRadius: "5px",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: () => ({
    display: "none",
  }),

  clearIndicator: () => ({
    display: "none",
  }),

  placeholder: (provided) => ({
    ...provided,
    fontSize: "12px",
  }),

  multiValue: (provided) => ({
    ...provided,
    fontSize: "12px",
  }),

  input: (provided) => ({
    ...provided,
    fontSize: "12px",
  }),
};

const DISABLED_INPUT_CLASSNAME = "!h-9 disabled:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed";

export interface TRFGenerateDetailsForm {
  customer_details: {
    name: string;
  };
  pet_name: string;
  emr_prognosis_status: string;
  referred_doctor: string;
  sender_clinic: string;
  destination_lab: string | null;
  lab_address: string | null;
  sample_collected_time: string;
  spoc_phone: string | null;
  spoc_email: string | null;
  requisition_numbers: string[];
  lab_tests: string[];
  trf_code: string;
  pre_requisite_forms: string[];
  mandatory_message: string | null;
  disable_button: boolean;
  vial_barcode: string[];
  requisition_vial_mapping: ITRFRequisitionVialMapping[];
}

interface IUploadLabReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requisitionId: string | null;
  uploadModalType: string | null;
  actionType: string | null;
  isDayClosing?: boolean;
}

export interface IUploadImagepayload {
  requisition_id: string;
  report_url: Array<string>;
}

interface IFormValues {
  report_url: Array<string>;
}

const TRFGenerateModal: React.FC<IUploadLabReportModalProps> = ({ open, setOpen, requisitionId, actionType }) => {
  // const initialState = useRef(false);
  const cancelButtonRef = useRef(null);
  const [loader, setLoader] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const { getRouteKey } = useRouter();
  const [loading, setLoading] = useState(false);
  const { execute: fetchemTRFGenerateDetailsData, TRFGenerateDetails } = useTRFGenerateAPI();
  const TRFUpdate = useTRFUpdateAPI();
  const { execute: generateVialBarcode } = useGenerateVialBarcodeAPI();
  const [generatingVial, setGeneratingVial] = useState<string | null>(null);
  const [submitAction, setSubmitAction] = useState<"download" | "print" | "generate" | null>(null);
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  // console.log("labReportData", labReportData);
  const isLabTestUrl = location.pathname.includes("lab-test");

  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();

  const formHook = useForm<TRFGenerateDetailsForm>({
    mode: "onChange",
    // defaultValues: { report_url: [] },
  });
  const {
    formState: { isDirty },
  } = formHook;

  const handleClose = () => {
    setOpen(false);
  };
  const LabType = (tab: string) => {
    if (tab === "incoming") {
      return 2;
    } else if (tab === "external") {
      return 1;
    } else if (tab === "internal") {
      return 3;
    }
  };

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const onSubmit = async (data: TRFGenerateDetailsForm) => {
    setLoading(true);
    const internalRequisitions = (data.requisition_vial_mapping ?? []).filter(
      (requisition) => requisition.requisition_type === "internal",
    );
    const mappedVialBarcodes = internalRequisitions.flatMap((requisition) =>
      (requisition.vial_type ?? []).map((vial) => vial.barcode?.trim()).filter(Boolean),
    );
    const constructedBody: any = {
      customer_details: { name: data.customer_details.name },
      pet_name: data.pet_name,
      emr_prognosis_status: data.emr_prognosis_status,
      referred_doctor: data.referred_doctor,
      sender_clinic: data.sender_clinic,
      destination_lab: data.destination_lab!,
      lab_address: data.lab_address!,
      sample_collected_time: data.sample_collected_time,
      spoc_phone: data.spoc_phone!,
      spoc_email: data.spoc_email!,
      requisition_numbers: data.requisition_numbers,
      lab_tests: data.lab_tests,
      trf_code: data.trf_code,
      vial_barcode: mappedVialBarcodes.length ? mappedVialBarcodes : (data.vial_barcode ?? []),
      pre_requisite_forms: data.pre_requisite_forms,
      mandatory_message: data.mandatory_message,
      // disable_button: data.disable_button,
    };
    if (internalRequisitions.length) {
      constructedBody.requisition_barcode_map = internalRequisitions.map((requisition) => ({
        requisition_number: requisition.requisition_number,
        barcodes: (requisition.vial_type ?? []).map((vial) => vial.barcode?.trim()).filter(Boolean),
        requisition_type: requisition.requisition_type,
        requisition_id: requisition.requisition_id,
        vial_barcode_mapping: (requisition.vial_type ?? []).map((vial) => ({
          vial_id: vial.id,
          vial_name: vial.name,
          barcode: [vial.barcode?.trim()].filter(Boolean),
        })),
      }));
    }
    const { status, data: responseData } = await TRFUpdate(requisitionId!, constructedBody, actionType);
    if (status && responseData?.pdf_url) {
      const payload = {
        start_date: labReportListFilter?.start_date,
        end_date: labReportListFilter?.end_date,
        status_ids: labReportListFilter?.status_ids,
        clinic_ids: labReportListFilter?.clinic_ids,
        lab_partner_ids: labReportListFilter?.lab_partner_ids,
        type: LabType(location.pathname.split("/").pop()),
      };
      processId &&
        navigate(
          `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
        );

      if (processId) {
        // Day closing: the navigate above bumps `t`, which makes the closing tab
        // refetch its own list. Fetching here too would overwrite it.
      } else if (isLabTestUrl) {
        const constructedBody = {
          start_date: labReportListFilter?.start_date,
          end_date: labReportListFilter?.end_date,
          customer_id: customerId,
          type: 3,
        };
        fetchLabReports(constructedBody);
      } else {
        fetchStatsList(payload);
        fetchLabReports(payload);
      }

      if (submitAction === "download") {
        downloadPdf(responseData.pdf_url);
      }
      if (submitAction === "print") {
        window.open(responseData.pdf_url, "_blank")?.print();
      }
      if (submitAction === "generate") {
        setOpen(false);
      }
    }
    setLoading(false);
    setSubmitAction(null);
  };

  useEffect(() => {
    if (requisitionId && actionType) {
      fetchemTRFGenerateDetailsData(requisitionId, actionType);
    }
  }, [requisitionId, actionType, fetchemTRFGenerateDetailsData]);
  const requisitionCount = TRFGenerateDetails?.requisition_numbers?.length || 0;

  useEffect(() => {
    if (!TRFGenerateDetails) return;
    formHook.setValue("customer_details.name", TRFGenerateDetails?.customer_details?.name ?? "");
    formHook.setValue("pet_name", TRFGenerateDetails?.pet_name ?? "");
    formHook.setValue("emr_prognosis_status", TRFGenerateDetails?.emr_prognosis_status ?? "");
    formHook.setValue("referred_doctor", TRFGenerateDetails?.referred_doctor ?? "");
    formHook.setValue("vial_barcode", TRFGenerateDetails?.vial_barcode ?? []);
    formHook.setValue(
      "requisition_vial_mapping",
      ((TRFGenerateDetails?.requisition_barcode_map ?? []) as any[])
        .filter((requisition) => requisition.requisition_type === "internal")
        .map((requisition) => ({
          ...requisition,
          vial_type: (requisition.vial_type ?? requisition.vial_barcode_mapping ?? []).map((vial) => ({
            id: vial.id ?? vial.vial_id,
            name: vial.name ?? vial.vial_name,
            barcode: (Array.isArray(vial.barcode) ? vial.barcode[0] : vial.barcode) ?? "",
          })),
        })),
    );
    formHook.setValue("sender_clinic", TRFGenerateDetails?.sender_clinic ?? "");
    formHook.setValue("destination_lab", TRFGenerateDetails?.destination_lab ?? null);
    formHook.setValue("lab_address", TRFGenerateDetails?.lab_address ?? null);
    formHook.setValue("sample_collected_time", TRFGenerateDetails?.sample_collected_time ?? "");
    formHook.setValue("spoc_phone", TRFGenerateDetails?.spoc_phone ?? null);
    formHook.setValue("spoc_email", TRFGenerateDetails?.spoc_email ?? null);
    formHook.setValue("requisition_numbers", TRFGenerateDetails?.requisition_numbers ?? []);
    formHook.setValue("lab_tests", TRFGenerateDetails?.lab_tests ?? []);
    formHook.setValue("trf_code", TRFGenerateDetails?.trf_code ?? "");
    formHook.setValue("pre_requisite_forms", TRFGenerateDetails?.pre_requisite_forms ?? []);
    formHook.setValue("mandatory_message", TRFGenerateDetails?.mandatory_message ?? null);
    formHook.setValue("disable_button", TRFGenerateDetails?.disable_button ?? false);
    formHook.reset(formHook.getValues(), { keepDirty: false });
    // setIsDirty(false);
  }, [TRFGenerateDetails]);

  const downloadPdf = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const vialBarcodes = formHook?.watch("vial_barcode");
  const vialMapping = formHook?.watch("requisition_vial_mapping");
  const hasVialMapping =
    Array.isArray(vialMapping) && vialMapping.some((requisition) => requisition.requisition_type === "internal");
  const hasBarcode = hasVialMapping
    ? vialMapping.every((requisition) =>
        requisition.vial_type.every((vial) => (vial.barcode?.trim()?.length ?? 0) >= 6),
      )
    : Array.isArray(vialBarcodes) && vialBarcodes.length > 0;
  const vialGroups = hasVialMapping
    ? [...new Set(vialMapping.flatMap((requisition) => requisition.vial_type.map((vial) => vial.id)))].map((vialId) => {
        const requisitions = vialMapping.filter((requisition) =>
          requisition.vial_type.some((vial) => vial.id === vialId),
        );
        return { vial: requisitions[0].vial_type.find((vial) => vial.id === vialId), requisitions };
      })
    : [];

  const handleGenerateBarcode = async (vialId: string) => {
    setGeneratingVial(vialId);
    const barcode = await generateVialBarcode();
    setGeneratingVial(null);
    if (!barcode) return;
    formHook.setValue(
      "requisition_vial_mapping",
      formHook.getValues("requisition_vial_mapping").map((requisition) => ({
        ...requisition,
        vial_type: requisition.vial_type.map((vial) => (vial.id === vialId ? { ...vial, barcode } : vial)),
      })),
      { shouldDirty: true },
    );
  };

  const showButton = TRFGenerateDetails?.show_button === true;
  const enableGenerate = showButton && hasBarcode && !loading;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => setOpen(false)}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <form
              onSubmit={formHook.handleSubmit(onSubmit)}
              className="inline-block w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
            >
              <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6">
                <Dialog.Title className="text-sm font-medium sm:text-base">TRF Generate</Dialog.Title>
                <button type="button" className="rounded-full p-1 text-gray-500 bg-gray-100 " onClick={handleClose}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto px-4 pb-4 pt-3 sm:px-6 sm:pt-2">
                {initialLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                    Loading certificate...
                  </div>
                ) : (
                  <>
                    <div className="bg-white  rounded-2xl space-y-10 ">
                      <div>
                        <h2 className="text-gray-500 text-sm mb-2">Basic Information </h2>

                        <div className="bg-[#F9FAFB] py-3 px-3 rounded-2xl">
                          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 bg-grey-500">
                            <div>
                              <div className="flex">
                                <p>Customer Name</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Customer name"
                                  register={formHook.register("customer_details.name", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.customer_details?.name}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Pet Name</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Pet name"
                                  register={formHook.register("pet_name", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.pet_name}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Pet Status(EMR Prognosis)</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Pet Status"
                                  register={formHook.register("emr_prognosis_status", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.emr_prognosis_status}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Referred Doctor</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Referred Doctor name"
                                  register={formHook.register("referred_doctor", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.referred_doctor}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <div className="flex">
                              {" "}
                              <p>Lab Tests</p>
                            </div>
                            <div className="bg-[#F9FAFB] py-2 rounded-lg">
                              <div className="flex flex-row flex-wrap gap-2">
                                {TRFGenerateDetails?.lab_tests?.length ? (
                                  <ol className="list-decimal list-inside space-y-1 text-xs font-medium text-[#696e79] bg-gray-100 p-2.5 rounded-md w-full border border-[#d9dbe1]">
                                    {TRFGenerateDetails.lab_tests.map((test, index) => (
                                      <li key={index}>{test}</li>
                                    ))}
                                  </ol>
                                ) : (
                                  <p className="text-xs text-gray-400">No lab tests available</p>
                                )}
                              </div>
                            </div>

                            <div className="mt-2">
                              {hasVialMapping ? (
                                <div className="space-y-2">
                                  {vialGroups.map(({ vial, requisitions }, index) => (
                                    <div
                                      key={vial.id ?? index}
                                      className="bg-white border border-[#d9dbe1] rounded-lg p-3"
                                    >
                                      {requisitions.map((requisition) => (
                                        <p
                                          key={requisition.requisition_id}
                                          className="text-xs font-medium text-[#374151]"
                                        >
                                          {requisition.test_name}
                                        </p>
                                      ))}

                                      <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-3 mt-2">
                                        <div>
                                          <p className="text-xs text-gray-500 mb-1">
                                            Vial {index + 1}
                                            {vial.name ? ` - ${vial.name}` : ""}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <input
                                              className="w-full h-9 rounded-md border border-[#d9dbe1] px-2 text-xs text-[#374151] bg-gray-100 cursor-not-allowed"
                                              placeholder="Barcode not generated"
                                              value={vial.barcode ?? ""}
                                              readOnly
                                            />
                                            {vial.barcode ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  printBarcode(
                                                    vial.barcode,
                                                    requisitions
                                                      .map((requisition) => requisition.requisition_number)
                                                      .join(", "),
                                                  )
                                                }
                                                className="shrink-0 w-[130px] h-9 inline-flex items-center justify-center gap-1 rounded-md bg-gray-800 px-3 text-xs font-medium text-white whitespace-nowrap hover:bg-gray-900"
                                              >
                                                <PrinterIcon className="h-4 w-4" />
                                                Print
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                disabled={generatingVial !== null}
                                                onClick={() => handleGenerateBarcode(vial.id)}
                                                className={clsx(
                                                  "shrink-[130px] h-9 inline-flex items-center justify-center rounded-md px-3 text-xs font-medium whitespace-nowrap",
                                                  generatingVial !== null
                                                    ? "bg-gray-800 text-white opacity-50 cursor-not-allowed"
                                                    : "bg-gray-800 text-white hover:bg-gray-900",
                                                )}
                                              >
                                                {generatingVial === vial.id ? "Generating..." : "Generate Barcode"}
                                              </button>
                                            )}
                                          </div>
                                          {vial.barcode && vial.barcode.trim().length < 6 ? (
                                            <p className="text-xs text-red-500 mt-1">
                                              Barcode must be at least 6 characters
                                            </p>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <>
                                  <div className="flex mb-2">
                                    <p>Vial Barcode</p>
                                    <span className="relative -top-1 -right-1 text-red-500 text-sm">*</span>

                                    <p className="text-xs text-gray-500 ml-2 flex justify-center items-center">
                                      ({requisitionCount} sample
                                      {requisitionCount !== 1 ? "s" : ""} {requisitionCount === 1 ? "is" : "are"} a part
                                      of this TRF)
                                    </p>
                                  </div>
                                  <div className="w-1/2 border rounded-lg">
                                    <Controller
                                      name="vial_barcode"
                                      control={formHook.control}
                                      rules={{
                                        required: "At least one vial barcode is required",
                                        validate: (value) =>
                                          value?.length > 0 || "At least one vial barcode is required",
                                      }}
                                      render={({ field }) => (
                                        <CreatableSelect
                                          isMulti
                                          value={(field.value || []).map((barcode: string) => ({
                                            label: barcode,
                                            value: barcode,
                                          }))}
                                          onChange={(options: any) => {
                                            const barcodes = options
                                              ? options.map((o: any) => o.value?.trim()).filter(Boolean)
                                              : [];
                                            field.onChange(barcodes);
                                          }}
                                          className="text-xs border-none"
                                          styles={customStyles}
                                          placeholder="Enter or scan vial barcodes"
                                          formatCreateLabel={(inputValue) => inputValue}
                                        />
                                      )}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl space-y-10 mt-2">
                      <div className="mt-3">
                        <h2 className="text-gray-500 text-sm mb-2">Other Details</h2>
                        <div className="bg-[#F9FAFB] py-3 px-2 rounded-2xl">
                          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 bg-grey-500">
                            <div>
                              <div className="flex">
                                <p>Requisition ID</p>
                              </div>
                              <div className="w-full mt-2">
                                <Controller
                                  name="requisition_numbers"
                                  control={formHook.control}
                                  render={({ field }) => (
                                    <CreatableSelect
                                      isMulti
                                      isDisabled
                                      isSearchable={false}
                                      menuIsOpen={false}
                                      value={(field.value || []).map((id: string) => ({
                                        label: id,
                                        value: id,
                                      }))}
                                      styles={{
                                        ...customStyles,
                                        control: (base) => ({
                                          ...base,
                                          backgroundColor: "#F3F4F6",
                                          border: "",
                                          cursor: "not-allowed",
                                          borderColor: "#D1D5DB",
                                          minHeight: "36px",
                                        }),
                                        multiValueRemove: () => ({
                                          display: "none",
                                        }),
                                      }}
                                      placeholder="Requisition ID"
                                    />
                                  )}
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>TRF Code</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Add TRF Code"
                                  register={formHook.register("trf_code", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.trf_code}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Sender Clinic</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Sender Clinic name"
                                  register={formHook.register("sender_clinic", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.sender_clinic}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Destination Lab</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Destination Lab name"
                                  register={formHook.register("destination_lab", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.destination_lab}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>
                            <div>
                              <div className="flex">
                                <p>Lab Address</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Lab Address"
                                  register={formHook.register("lab_address", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.lab_address}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>

                            <div>
                              <div className="flex">
                                <p>Sample Collected Time</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Sample Collected Time"
                                  register={formHook.register("sample_collected_time", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.sample_collected_time}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>
                            <div>
                              <div className="flex">
                                <p>SPOC Phone</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter SPOC Phone Number"
                                  register={formHook.register("spoc_phone", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.spoc_phone}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>
                            <div>
                              <div className="flex">
                                <p>Central SPOC Email</p>
                              </div>
                              <div className="w-full mt-2">
                                <Input
                                  className="text-xs"
                                  inputClassName={DISABLED_INPUT_CLASSNAME}
                                  placeholder="Enter Email"
                                  register={formHook.register("spoc_email", {
                                    required: false,
                                  })}
                                  fieldError={formHook.formState.errors?.spoc_email}
                                  errorMessages={[{ message: "", type: "required" }]}
                                  disabled
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl space-y-10 mt-3">
                      <div>
                        <h2 className="text-gray-500 text-sm mb-2">Pre - Requisite Forms</h2>
                        {TRFGenerateDetails?.pre_requisite_forms?.length > 0 ? (
                          <div className="bg-[#F9FAFB] py-2 px-2 rounded-lg">
                            <div className="flex flex-row flex-wrap gap-2">
                              {TRFGenerateDetails.pre_requisite_forms.map((form, index) => (
                                <p key={index} className="bg-[#EFEFEF] p-2 rounded-lg text-xs">
                                  {form}
                                </p>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">No pre-requisite forms</p>
                        )}
                      </div>
                    </div>

                    {TRFGenerateDetails?.mandatory_message?.length ? (
                      <div className="p-3 rounded-2xl space-y-10 mt-2">
                        <div className="flex flex-row flex-wrap gap-2">
                          <p className="text-xs text-red-500">{TRFGenerateDetails.mandatory_message}</p>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div className="flex justify-end border-t bg-[#F9FAFB] px-4 py-3 sm:px-6 gap-2">
                <button
                  type="submit"
                  onClick={() => setSubmitAction("generate")}
                  disabled={!enableGenerate}
                  className={clsx(PRIMARY_BUTTON, enableGenerate ? "" : "opacity-50 cursor-not-allowed")}
                >
                  {loading ? "Generating..." : "Generate Form"}
                </button>
              </div>
            </form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default TRFGenerateModal;
